import React, { useEffect, useState, Component } from 'react';
import { XIcon, CheckIcon } from 'lucide-react';
export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });
  useEffect(() => {
    const consent = document.cookie.split('; ').find(row => row.startsWith('2n_cookie_consent='));
    if (!consent) {
      // Small delay before showing to ensure smooth page load
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPrefs = JSON.parse(decodeURIComponent(consent.split('=')[1]));
        setPreferences(savedPrefs);
      } catch (e) {
        setIsVisible(true);
      }
    }
  }, []);
  const saveConsent = (prefs: typeof preferences) => {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `2n_cookie_consent=${encodeURIComponent(JSON.stringify(prefs))}; expires=${date.toUTCString()}; path=/`;
    setPreferences(prefs);
    setIsVisible(false);
    setShowSettings(false);
  };
  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true
    });
  };
  const rejectAll = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false
    });
  };
  const saveSettings = () => {
    saveConsent(preferences);
  };
  if (!isVisible && !showSettings) return null;
  return <>
      {/* Main Banner */}
      {isVisible && !showSettings && <div className="fixed bottom-0 left-0 right-0 z-[100] bg-[#1a1a1a] border-t-4 border-[#59d0d4] shadow-2xl animate-springUp" data-id="element-278">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8" data-id="element-279">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-id="element-280">
              <div className="flex-1" data-id="element-281">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-282">
                  Wir verwenden Cookies
                </h3>
                <p className="text-[#748282] text-sm leading-relaxed max-w-3xl" data-id="element-283">
                  Um Ihnen das beste Erlebnis auf unserer Website zu bieten,
                  verwenden wir Cookies. Einige sind essenziell für den Betrieb
                  der Seite, während andere uns helfen, diese Website und Ihre
                  Erfahrung zu verbessern. Sie können selbst entscheiden, ob Sie
                  die Cookies zulassen möchten.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto shrink-0" data-id="element-284">
                <button onClick={() => setShowSettings(true)} className="text-[#59d0d4] hover:text-white text-sm font-semibold underline underline-offset-4 transition-colors px-4 py-2" data-id="element-285">
                  Einstellungen
                </button>
                <button onClick={rejectAll} className="w-full sm:w-auto px-6 py-2.5 border border-[#748282] text-white rounded hover:bg-white/5 transition-colors font-semibold text-sm" data-id="element-286">
                  Ablehnen
                </button>
                <button onClick={acceptAll} className="w-full sm:w-auto px-6 py-2.5 bg-[#3b7780] hover:bg-[#2d5f66] text-white rounded transition-colors font-bold text-sm shadow-lg" data-id="element-287">
                  Alle akzeptieren
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Settings Panel Overlay */}
      {showSettings && <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm animate-fadeIn flex justify-end" data-id="element-288">
          <div className="w-full max-w-md bg-white h-full shadow-2xl animate-slideInRight flex flex-col" data-id="element-289">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50" data-id="element-290">
              <h2 className="text-xl font-bold text-slate-900" data-id="element-291">
                Cookie-Einstellungen
              </h2>
              <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-slate-900 transition-colors p-2" data-id="element-292">
                <XIcon className="w-6 h-6" data-id="element-293" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-294">
              {/* Essential */}
              <div className="flex gap-4" data-id="element-295">
                <div className="mt-1" data-id="element-296">
                  <div className="w-12 h-6 bg-[#3b7780] rounded-full relative opacity-70 cursor-not-allowed" data-id="element-297">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full flex items-center justify-center" data-id="element-298">
                      <CheckIcon className="w-3 h-3 text-[#3b7780]" data-id="element-299" />
                    </div>
                  </div>
                </div>
                <div data-id="element-300">
                  <h4 className="font-bold text-slate-900 mb-1" data-id="element-301">
                    Essenzielle Cookies
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-302">
                    Notwendig für die Grundfunktionen der Website. Diese können
                    nicht deaktiviert werden.
                  </p>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex gap-4" data-id="element-303">
                <div className="mt-1" data-id="element-304">
                  <button onClick={() => setPreferences(p => ({
                ...p,
                analytics: !p.analytics
              }))} className={`w-12 h-6 rounded-full relative transition-colors ${preferences.analytics ? 'bg-[#3b7780]' : 'bg-slate-300'}`} data-id="element-305">
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${preferences.analytics ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-306" />
                  </button>
                </div>
                <div data-id="element-307">
                  <h4 className="font-bold text-slate-900 mb-1" data-id="element-308">
                    Analyse-Cookies
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-309">
                    Helfen uns, die Nutzung der Website zu verstehen und zu
                    verbessern.
                  </p>
                </div>
              </div>

              {/* Marketing */}
              <div className="flex gap-4" data-id="element-310">
                <div className="mt-1" data-id="element-311">
                  <button onClick={() => setPreferences(p => ({
                ...p,
                marketing: !p.marketing
              }))} className={`w-12 h-6 rounded-full relative transition-colors ${preferences.marketing ? 'bg-[#3b7780]' : 'bg-slate-300'}`} data-id="element-312">
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${preferences.marketing ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-313" />
                  </button>
                </div>
                <div data-id="element-314">
                  <h4 className="font-bold text-slate-900 mb-1" data-id="element-315">
                    Marketing-Cookies
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-316">
                    Werden für personalisierte Werbung und Retargeting
                    verwendet.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50" data-id="element-317">
              <button onClick={saveSettings} className="w-full py-3 bg-[#3b7780] hover:bg-[#2d5f66] text-white font-bold rounded transition-colors shadow-md" data-id="element-318">
                Auswahl speichern
              </button>
            </div>
          </div>
        </div>}
    </>;
}